import { useCallback } from 'react';
import { useAppState, useAppActions } from '../context/AppContext';
import { service, handleApiError } from '../services';
import { HealthProfessional, CallRequestData } from '../types';

/**
 * Custom hook for managing call request flow and state
 */
export const useCallRequest = () => {
  const state = useAppState();
  const actions = useAppActions();

  const {
    selectedHealthProfessional,
    callRequestData,
    callConfirmation,
    isLoading,
    error
  } = state;

  // Select a health professional and navigate to call request
  const selectProfessional = useCallback((professional: HealthProfessional) => {
    actions.setSelectedProfessional(professional);
    actions.setView('callRequest');
    actions.clearError();
  }, [actions]);

  // Submit call request
  const submitCallRequest = useCallback(async (requestData: CallRequestData) => {
    let professionalToUse = selectedHealthProfessional;
    
    // If no professional is selected, auto-select the first available one
    if (!professionalToUse) {
      try {
        actions.setLoading(true);
        const availableProfessionals = await service.healthProfessionals.getAvailable();
        
        if (availableProfessionals.length === 0) {
          actions.setError('No health professionals are currently available. Please try again later.');
          return false;
        }
        
        professionalToUse = availableProfessionals[0];
        actions.setSelectedProfessional(professionalToUse);
      } catch (err) {
        const errorMessage = handleApiError(err);
        actions.setError(`Failed to find available health professional: ${errorMessage}`);
        return false;
      }
    }

    actions.setLoading(true);
    actions.clearError();

    try {
      // Store the request data
      actions.setCallRequestData(requestData);

      // Submit to API
      const confirmation = await service.callRequests.submit(
        professionalToUse.id,
        requestData
      );

      // Store confirmation and navigate
      actions.setCallConfirmation(confirmation);
      actions.setView('confirmation');
      
      return true;
    } catch (err) {
      const errorMessage = handleApiError(err);
      actions.setError(`Failed to submit call request: ${errorMessage}`);
      return false;
    } finally {
      actions.setLoading(false);
    }
  }, [selectedHealthProfessional, actions]);

  // Cancel call request and return to landing
  const cancelCallRequest = useCallback(() => {
    actions.setView('landing');
    actions.setSelectedProfessional(null);
    actions.clearError();
  }, [actions]);

  // Start over - return to landing page and clear all data
  const startOver = useCallback(() => {
    actions.startOver();
  }, [actions]);

  // Navigate back to landing from confirmation
  const backToLanding = useCallback(() => {
    actions.setView('landing');
    actions.setSelectedProfessional(null);
    actions.setCallRequestData(null);
    actions.setCallConfirmation(null);
    actions.clearError();
  }, [actions]);

  // Retry failed request
  const retryRequest = useCallback(async () => {
    if (!callRequestData || !selectedHealthProfessional) {
      actions.setError('No request data to retry');
      return false;
    }

    return await submitCallRequest(callRequestData);
  }, [callRequestData, selectedHealthProfessional, submitCallRequest, actions]);

  // Get call request status
  const getRequestStatus = useCallback(async (requestId: string) => {
    actions.setLoading(true);
    actions.clearError();

    try {
      const confirmation = await service.callRequests.getById(requestId);
      actions.setCallConfirmation(confirmation);
      return confirmation;
    } catch (err) {
      const errorMessage = handleApiError(err);
      actions.setError(`Failed to get request status: ${errorMessage}`);
      return null;
    } finally {
      actions.setLoading(false);
    }
  }, [actions]);

  // Validate call request data
  const validateRequestData = useCallback((requestData: CallRequestData): string[] => {
    const errors: string[] = [];

    if (!requestData.symptoms || requestData.symptoms.length === 0) {
      errors.push('At least one symptom must be selected');
    }

    if (!requestData.patientPhone || requestData.patientPhone.trim().length === 0) {
      errors.push('Phone number is required');
    } else {
      // Basic phone validation
      const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
      const cleanPhone = requestData.patientPhone.replace(/[\s\-().]/g, '');
      if (!phoneRegex.test(cleanPhone) || cleanPhone.length < 10) {
        errors.push('Please enter a valid phone number (at least 10 digits)');
      }
    }

    return errors;
  }, []);

  return {
    // State
    selectedHealthProfessional,
    callRequestData,
    callConfirmation,
    isLoading,
    error,

    // Actions
    selectProfessional,
    submitCallRequest,
    cancelCallRequest,
    startOver,
    backToLanding,
    retryRequest,
    getRequestStatus,
    validateRequestData,

    // Computed values
    canSubmitRequest: !!selectedHealthProfessional && !isLoading,
    hasActiveRequest: !!callRequestData,
    hasConfirmation: !!callConfirmation,
    requestId: callConfirmation?.requestId || null,
  };
};